import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { countryLabel } from "@/constants/countries";
import { FiUsers, FiEye, FiCheckCircle, FiTrendingUp, FiMapPin, FiClock, FiArrowRight } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const translations = {
  en: {
    title: "Dashboard",
    workspace: {
      title: "Setup your workspace",
      subtitle: "Complete each area to unlock Jobego's full potential.",
      completed: "Completed",
      in_progress: "In progress",
      not_started: "Not started",
      edit: "Edit",
      setup: "Set up",
      continue: "Continue",
      company_profile: "Company Profile",
      company_profile_desc: "Name, industry, location, description",
      career_page: "Career Page",
      career_page_desc: "Public page at jobego.co/",
      culture_profile: "Culture Profile",
      culture_profile_desc: "Dilemmas define your culture radar",
      complete: "Complete",
      published: "Published",
      not_connected: "Not connected",
      answered: "answered",
    },
    chart: {
      title: "Engagement Statistics",
      subtitle: "(last 30 days)",
      applications: "Applications",
      job_views: "Job Views",
      interviews: "Interviews",
      offers: "Offers",
    },
    metrics: {
      total_applications: "Total Applications",
      total_job_views: "Job Views",
      total_interviews: "Interviews Scheduled",
      total_offers: "Offers Made",
    },
    candidates_review: {
      title: "Latest Applications",
      view_all: "View All",
      no_candidates: "No candidates to review",
      no_candidates_desc: "New applications will appear here",
      new_badge: "New",
      review: "Review",
      create_job: "Create Your First Job",
    },
    active_jobs: {
      title: "Active Job Listings",
      view_all: "View All Jobs",
      no_jobs: "No active jobs",
      no_jobs_desc: "Create a job ad to start recruiting",
      create_job: "Create Job Ad",
      candidates_count: "candidates",
      view_job: "View",
    },
    error_fetch: "Error loading dashboard data",
  },
  fr: {
    title: "Tableau de bord",
    workspace: {
      title: "Configurez votre espace",
      subtitle: "Complétez chaque section pour débloquer tout le potentiel de Jobego.",
      completed: "Complété",
      in_progress: "En cours",
      not_started: "Non commencé",
      edit: "Modifier",
      setup: "Configurer",
      continue: "Continuer",
      company_profile: "Profil Entreprise",
      company_profile_desc: "Nom, secteur, localisation, description",
      career_page: "Page Carrière",
      career_page_desc: "Page publique sur jobego.co/",
      culture_profile: "Profil Culture",
      culture_profile_desc: "Les dilemmes définissent votre radar culturel",
      complete: "Complété",
      published: "Publié",
      not_connected: "Non connecté",
      answered: "répondu",
    },
    chart: {
      title: "Statistiques d'engagement",
      subtitle: "(30 derniers jours)",
      applications: "Candidatures",
      job_views: "Vues d'offres",
      interviews: "Entretiens",
      offers: "Offres",
    },
    metrics: {
      total_applications: "Total des candidatures",
      total_job_views: "Vues d'offres",
      total_interviews: "Entretiens planifiés",
      total_offers: "Offres faites",
    },
    candidates_review: {
      title: "Dernières candidatures",
      view_all: "Voir tout",
      no_candidates: "Aucun candidat à examiner",
      no_candidates_desc: "Les nouvelles candidatures apparaîtront ici",
      new_badge: "Nouveau",
      review: "Examiner",
      create_job: "Créer votre première offre",
    },
    active_jobs: {
      title: "Offres d'emploi actives",
      view_all: "Voir toutes les offres",
      no_jobs: "Aucune offre active",
      no_jobs_desc: "Créez une offre d'emploi pour commencer à recruter",
      create_job: "Créer une offre",
      candidates_count: "candidats",
      view_job: "Voir",
    },
    error_fetch: "Erreur lors du chargement des données",
  },
};

import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";

const Dashboard = () => {
  const { t } = useComponentTranslation(translations);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { user, organization } = useStore();
  const [metrics, setMetrics] = useState({
    totalApplications: 0,
    totalJobViews: 0,
    totalInterviews: 0,
    totalOffers: 0,
  });
  const [candidatesToReview, setCandidatesToReview] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);

  const formatDate = (date) => {
    if (!date) return "";
    const now = new Date();
    const appliedDate = new Date(date);
    const diffTime = Math.abs(now - appliedDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

    if (diffDays === 0) {
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return `${diffMinutes} min ago`;
      }
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    }
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  };

  const formatJobDate = (date) => {
    if (!date) return "";
    const now = new Date();
    const publishedDate = new Date(date);
    const diffTime = Math.abs(now - publishedDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch statistics
        const statsResponse = await api.post("/statistics/search", {
          organization_id: organization?._id, organization_name: organization?.name,
        });

        if (!statsResponse.ok) throw statsResponse;
        if (statsResponse.data) {
          const stats = statsResponse.data;
          setMetrics({
            totalApplications: stats.candidates?.total || 0,
            totalJobViews: stats.totalViews || 0,
            totalInterviews: stats.candidates?.selected || 0,
            totalOffers: stats.candidates?.recruited || 0,
          });
        }

        // Fetch candidates to review (recent APPLIED status)
        const candidatesResponse = await api.post("/application/search", {
          limit: 5,
          status: ["APPLIED"],
          organization_id: organization?._id, organization_name: organization?.name,
        });

        if (!candidatesResponse.ok) throw candidatesResponse;
        if (candidatesResponse.data) {
          const candidates = candidatesResponse.data.map((candidate) => ({
            id: candidate._id,
            jobId: candidate.job_id,
            jobTitle: candidate.job_title || "Unknown Job",
            name: candidate.candidate_name || `${candidate.firstname || ""} ${candidate.lastname || ""}`.trim() || "Unknown Candidate",
            location: candidate.location,
            score: candidate.global_matching_score || 0,
            isNew: true, // All are new since we're filtering by APPLIED
            appliedDate: formatDate(candidate.created_at || candidate.application_date),
          }));
          setCandidatesToReview(candidates);
        }

        // Fetch active jobs
        const jobsResponse = await api.post("/job/search", {
          organization_id: organization?._id, organization_name: organization?.name,
          limit: 4,
          status: "PUBLISHED",
        });

        if (!jobsResponse.ok) throw jobsResponse;
        if (jobsResponse.data) {
          const jobIds = jobsResponse.data.map((job) => job._id);

          // Fetch candidate counts for these jobs
          let candidatesCounts = {};
          if (jobIds.length > 0) {
            const candidatesCountResponse = await api.post("/application/aggregate", {
              job_id: jobIds,
              status: ["APPLIED", "REFUSED", "PRESELECTED", "INTERVIEW", "SELECTED"],
            });

            if (!candidatesCountResponse.ok) throw candidatesCountResponse;
            if (candidatesCountResponse.data) {
              candidatesCounts = (candidatesCountResponse.data.job_id || []).reduce((acc, item) => {
                acc[item._id] = item.count;
                return acc;
              }, {});
            }
          }

          const jobs = jobsResponse.data.map((job) => {
            const totalCandidates = candidatesCounts[job._id] || 0;
            // Get new candidates count (APPLIED status in last 7 days)
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            return {
              id: job._id,
              title: job.title,
              location: job.city ? `${job.city}${job.country ? `, ${countryLabel(job.country)}` : ""}` : "Remote",
              candidatesCount: totalCandidates,
              newCandidates: 0, // Would need additional query to get this
              status: job.status,
              publishedDate: formatJobDate(job.publication_date || job.created_at),
            };
          });
          setActiveJobs(jobs);
        }
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
        toast.error(e.code);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, organization?._id]);

  const workspaceItems = useMemo(() => {
    const org = organization || {};
    const dilemmasCount = org.culture_dilemmas?.length || 0;
    const cultureStatus = org.culture_profile?.status;

    const hasCompanyProfile = !!(org.name && org.description);
    const hasCareerPage = !!(org.career_website_slug);
    const hasCultureDone = cultureStatus === "done";
    const hasCultureStarted = dilemmasCount > 0;
    return [
      {
        icon: "🏢",
        label: t("workspace.company_profile"),
        description: t("workspace.company_profile_desc"),
        status: hasCompanyProfile ? "complete" : "not_started",
        statusLabel: hasCompanyProfile ? t("workspace.complete") : t("workspace.not_started"),
        actionLabel: hasCompanyProfile ? t("workspace.edit") : t("workspace.setup"),
        to: "/recruiter/organisation/company",
      },
      {
        icon: "🌐",
        label: t("workspace.career_page"),
        description: `${t("workspace.career_page_desc")}${org.career_website_slug || ""}`,
        status: hasCareerPage ? "complete" : "not_started",
        statusLabel: hasCareerPage ? t("workspace.published") : t("workspace.not_started"),
        actionLabel: hasCareerPage ? t("workspace.edit") : t("workspace.setup"),
        to: "/recruiter/organisation/career",
      },
      {
        icon: "💜",
        label: t("workspace.culture_profile"),
        description: t("workspace.culture_profile_desc"),
        status: hasCultureDone ? "complete" : hasCultureStarted ? "in_progress" : "not_started",
        statusLabel: hasCultureDone ? t("workspace.complete") : hasCultureStarted ? `${dilemmasCount}/6 ${t("workspace.answered")}` : t("workspace.not_started"),
        actionLabel: hasCultureDone ? t("workspace.edit") : hasCultureStarted ? t("workspace.continue") : t("workspace.setup"),
        to: "/recruiter/organisation/culture",
      },
    ];
  }, [organization, t]);

  const completedCount = workspaceItems.filter((i) => i.status === "complete").length;
  const inProgressCount = workspaceItems.filter((i) => i.status === "in_progress").length;
  const notStartedCount = workspaceItems.filter((i) => i.status === "not_started").length;

  if (loading) return <Loader />;

  return (
    <div className="p-6">
      {/* Workspace Setup */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t("workspace.title")}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t("workspace.subtitle")}</p>
          </div>
          <div className="flex items-center gap-4 text-xs font-medium">
            {completedCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                {completedCount} {t("workspace.completed")}
              </span>
            )}
            {inProgressCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                {inProgressCount} {t("workspace.in_progress")}
              </span>
            )}
            {notStartedCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-300" />
                {notStartedCount} {t("workspace.not_started")}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {workspaceItems.map((item) => (
            <div
              key={item.to}
              onClick={() => navigate(item.to)}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-primary hover:shadow-sm transition-all cursor-pointer group"
            >
              <span className="text-2xl shrink-0">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                      item.status === "complete"
                        ? "bg-green-100 text-green-700"
                        : item.status === "in_progress"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {item.statusLabel}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 truncate">{item.label}</h3>
                <p className="text-xs text-gray-500 truncate">{item.description}</p>
              </div>
              <span className="text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 flex items-center gap-0.5">
                {item.actionLabel} <FiArrowRight className="w-3 h-3" />
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Views Graph */}
      <ViewsGraph organizationId={organization?._id} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Job Listings */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t("active_jobs.title")}</h3>
            <button onClick={() => navigate("/recruiter/job-ads?adStatus=PUBLISHED")} className="text-sm text-primary hover:text-primary-hover font-medium">
              {t("active_jobs.view_all")} →
            </button>
          </div>

          <div className="space-y-3">
            {activeJobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiCheckCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium mb-1">{t("active_jobs.no_jobs")}</p>
                <p className="text-xs text-gray-400 mb-4">{t("active_jobs.no_jobs_desc")}</p>
                <button onClick={() => navigate("/recruiter/job-ads")} className="btn btn-primary btn-small">
                  {t("active_jobs.create_job")}
                </button>
              </div>
            ) : (
              activeJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => navigate(`/recruiter/job-ads/${job.id}`)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">{job.title}</h4>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <FiMapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{job.location}</span>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full shrink-0 ml-2">{job.status}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <FiUsers className="w-4 h-4" />
                        <span className="font-medium">{job.candidatesCount}</span>
                        <span>{t("active_jobs.candidates_count")}</span>
                      </div>
                      {job.newCandidates > 0 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">+{job.newCandidates} new</span>}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <FiClock className="w-3 h-3" />
                      <span>{job.publishedDate}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Last Candidates */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t("candidates_review.title")}</h3>
            <button onClick={() => navigate("/recruiter/pool?status=APPLIED")} className="text-sm text-primary hover:text-primary-hover font-medium">
              {t("candidates_review.view_all")} →
            </button>
          </div>

          <div className="space-y-3">
            {candidatesToReview.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiUsers className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium mb-1">{t("candidates_review.no_candidates")}</p>
                <p className="text-xs text-gray-400 mb-4">{t("candidates_review.no_candidates_desc")}</p>
              </div>
            ) : (
              candidatesToReview.map((candidate) => (
                <div
                  key={candidate.id}
                  onClick={() => navigate(`/recruiter/job-ads/${candidate.jobId}?candidateId=${candidate.id}`)}
                  className="p-3 border border-gray-200 rounded-lg hover:border-primary hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{candidate.name}</h4>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <span className="truncate">{candidate.jobTitle}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0 ml-2 whitespace-nowrap">{candidate.appliedDate}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Views Graph Component
function ViewsGraph({ organizationId }) {
  const [stats, setStats] = useState([]);
  const [channels, setChannels] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [days] = useState(30); // Default to 30 days

  // Color palette for channels
  const channelColors = {
    0: "#3b82f6", // blue
    1: "#a855f7", // purple
    2: "#f97316", // orange
    3: "#ec4899", // pink
    4: "#10b981", // green
  };

  useEffect(() => {
    fetchViewStats();
  }, [organizationId, days]);

  async function fetchViewStats() {
    try {
      setLoading(true);

      // Fetch views for the last N days (default 30)
      const daysAgo = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

      const viewsResponse = await api.post("/view/search", {
        organization_id: organizationId,
        from_date: daysAgo.toISOString(),
        limit: 10000, // Get all views for the period
      });

      if (!viewsResponse.ok) throw viewsResponse;
      if (viewsResponse.data) {
        const views = viewsResponse.data;

        // Generate all dates for the last N days
        const allDates = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = days - 1; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateKey = date.toISOString().split("T")[0];
          allDates[dateKey] = { date: dateKey };
        }

        // Group views by date and channel
        const channelTotals = {};
        const channelsSet = new Set();

        views.forEach((view) => {
          const date = new Date(view.created_at).toISOString().split("T")[0];
          const channelName = view.channel_name || "Direct";

          channelsSet.add(channelName);

          // Initialize date if not already in allDates (shouldn't happen, but safety check)
          if (!allDates[date]) {
            allDates[date] = { date };
          }

          // Count views per channel per date
          allDates[date][channelName] = (allDates[date][channelName] || 0) + 1;

          // Track total channel counts
          channelTotals[channelName] = (channelTotals[channelName] || 0) + 1;
        });

        // Initialize all dates with 0 for all channels
        const sortedChannels = Array.from(channelsSet).sort((a, b) => (channelTotals[b] || 0) - (channelTotals[a] || 0));
        Object.keys(allDates).forEach((dateKey) => {
          sortedChannels.forEach((channel) => {
            if (!allDates[dateKey][channel]) {
              allDates[dateKey][channel] = 0;
            }
          });
        });

        // Convert to array and sort by date
        const chartData = Object.values(allDates).sort((a, b) => new Date(a.date) - new Date(b.date));

        // Format dates for better display
        const formattedChartData = chartData.map((item) => {
          const date = new Date(item.date);
          return {
            ...item,
            formattedDate: `${date.getDate()}/${date.getMonth() + 1}`,
            fullDate: date.toLocaleDateString("en-US", { day: "numeric", month: "short" }),
          };
        });

        setStats(formattedChartData);
        setChannels(sortedChannels);
        setTotals(channelTotals);
      }
    } catch (e) {
      console.error("Error fetching view stats:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const totalViews = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      return (
        <div className="bg-white p-4 shadow-2xl rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{payload[0]?.payload?.fullDate}</p>
          {payload.map(
            (entry, index) =>
              entry.value > 0 && (
                <p key={`item-${index}`} style={{ color: entry.fill }} className="text-sm font-medium">
                  {entry.name}: <span className="font-bold">{entry.value}</span>
                </p>
              )
          )}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <p className="text-sm text-gray-600">
              Total: <span className="font-bold text-gray-900">{totalViews}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <Loader size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          Engagement Statistics <span className="text-sm text-gray-500 font-normal">(last {days} days)</span>
        </h2>
      </div>

      {!stats || stats.length === 0 || channels.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          <p className="text-lg font-semibold mb-2">No data available</p>
          <p className="text-sm">We haven't tracked any engagement for your jobs yet.</p>
        </div>
      ) : (
        <div className="h-[220px] mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats} margin={{ top: 10, right: 0, left: -20, bottom: 0 }} barGap={0} barCategoryGap="15%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="formattedDate" tick={{ fill: "#6b7280", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ paddingLeft: "20px" }}
                iconType="circle"
                formatter={(value) => <span style={{ color: "#4b5563", fontSize: 12 }}>{value}</span>}
              />
              {channels.slice(0, 5).map((channel, index) => (
                <Bar key={channel} dataKey={channel} name={channel} fill={channelColors[index % Object.keys(channelColors).length]} radius={[4, 4, 0, 0]} maxBarSize={50} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
